<template>
  <div class="datetime-picker">
    <div class="datetime-picker__layout">
      <div
        v-if="isUnavailableDay"
        class="datetime-picker__unavailable"
        role="alert"
      >
        This venue is unavailable on the selected day. Please choose another date.
      </div>

      <div class="datetime-picker__quick-dates">
        <p class="datetime-picker__quick-heading">Select Date</p>
        <div class="datetime-picker__quick-scroll">
          <button
            v-for="day in nextSevenDays"
            :key="day.key"
            type="button"
            class="datetime-picker__quick-btn"
            :class="{
              'datetime-picker__quick-btn--active': isSameCalendarDay(day.date, date),
            }"
            :disabled="day.disabled"
            @click="selectQuickDate(day.date)"
          >
            <span class="datetime-picker__quick-dow">{{ day.dow }}</span>
            <span class="datetime-picker__quick-num">{{ day.dayNum }}</span>
          </button>
        </div>
      </div>

      <div class="datetime-picker__calendar">
        <label class="datetime-picker__label datetime-picker__label--calendar">Calendar</label>
        <div class="datetime-picker__control datetime-picker__control--calendar">
          <DatePicker
            ref="datePicker"
            mode="date"
            title="Select date"
            v-model="date"
            :minDate="computedMinDate"
            :maxDate="computedMaxDate"
            @click="getValidHoursByDay({ preserveTimes: true })"
            @change="onDateChange"
            @input="onDateChange"
            @update:modelValue="onDateChange"
            :options="false"
          />
        </div>
      </div>

      <template v-if="!isUnavailableDay">
        <div v-if="validFullday == false" class="datetime-picker__quick-slots">
          <p class="datetime-picker__quick-heading">Suggested times</p>
          <div v-if="quickTimeSlots.length" class="datetime-picker__quick-slots-grid">
            <button
              v-for="slot in quickTimeSlots"
              :key="slot.id"
              type="button"
              class="datetime-picker__quick-slot-btn"
              :class="{
                'datetime-picker__quick-slot-btn--active': isQuickSlotSelected(slot),
              }"
              :aria-label="slot.label"
              @click="selectQuickSlot(slot, { fromUser: true })"
            >
              <span class="datetime-picker__quick-slot-time">{{ slot.startLabel }}</span>
            </button>
          </div>
          <p v-else-if="hasNoAvailableSlots" class="datetime-picker__no-slots">
            {{ noSlotsMessage }}
          </p>
        </div>

        <div class="datetime-picker__side">
          <div v-if="validFullday == false" class="datetime-picker__side-stack">
            <div class="datetime-picker__times">
              <div class="datetime-picker__time">
                <label class="datetime-picker__label">Start time</label>
                <div class="datetime-picker__control datetime-picker__control--time">
                  <DatePicker
                    mode="time"
                    title="Start time"
                    v-model="startTime"
                    :valid-hours="validateHours"
                    :is24hr="true"
                    :minute-increment="30"
                    @change="onCustomTimeChange()"
                  />
                </div>
              </div>

              <div class="datetime-picker__time">
                <label class="datetime-picker__label">End time</label>
                <div class="datetime-picker__control datetime-picker__control--time">
                  <DatePicker
                    mode="time"
                    title="End time"
                    v-model="endTime"
                    :valid-hours="validateEndHours"
                    :is24hr="true"
                    :minute-increment="endMinuteIncrement"
                    :key="endTimePickerKey"
                    @update:modelValue="onEndTimeUpdate"
                    @change="onEndTimeChange"
                  />
                </div>
              </div>
            </div>

            <div v-if="$slots['sidebar-extra']" class="datetime-picker__people-row">
              <slot name="sidebar-extra" />
            </div>
          </div>

          <div v-else-if="$slots['sidebar-extra']" class="datetime-picker__extra">
            <slot name="sidebar-extra" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  getBookingGeneral,
} from "../../../store/vuexStore";
import * as ENV from "@/config.js";
import { getVenueWorkingHours } from "../../../../server/config";
import {
  findFirstDateWithAvailableSlots,
  getAvailableHourSlots,
} from "@/utils/quickTimeSlots.js";
import { BOOKING_LOCALE, formatLocalDateKey, getBookingWeekdayName } from "@/utils/bookingDateTime.js";

export default {
  data() {
    return {
      startTime: new Date(
        new Date("2012/10/09 00:00").setHours(new Date().getHours())
      ),
      endTime: new Date(
        new Date("2012/10/09 00:00").setHours(new Date().getHours() + 1)
      ),
      date: this.getInitialDate(),
      validateHours: [],
      workingHoursConfig: {},
      showError: false,
      invalidBooking: false,
      errorMessageTime: "",
      timeErrorType: null, // 'start_before_opening' | 'end_after_closing' | 'no_slots_available'
      validFullday: false,
      bookingTimeError: false,
      bookingTimeErrorMessage: "",
      workingHoursArray: [],
      Weekdays: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      facility: ENV.VUE_APP_FACILITY,
      userHasCustomizedSchedule: false,
      hasAppliedInitialSchedule: false,
      scheduledBeforeBookedEventsLoaded: false,
    };
  },

  props: ["index", "addedVenue", "checkValidation", "selectedElement", "initialBookingAllowed", "additionalBookingAllowed", "otpUserBookingHours", "selectedDate", "minDaysInAdvance", "daysInAdvanceEnabled", "minHoursInAdvance", "hoursInAdvanceEnabled", "bookedEvents"],

  watch: {
    checkValidation(value) {
      this.validFullday = value;
    },
    selectedDate(newValue) {
      if (!newValue) return;
      const [day, month, year] = newValue.split("/");
      const parsed = new Date(year, month - 1, day);
      if (Number.isNaN(parsed.getTime())) return;
      if (!this.isSameCalendarDay(parsed, this.date)) {
        this.date = parsed;
      }
    },
    date(newDate, oldDate) {
      if (newDate && newDate !== oldDate) {
        const formattedDate = this.formatDateForAPI(newDate);
        this.$emit('dateSelected', formattedDate);
      }
    },

    addedVenue() {
      if (this.addedVenue?.StartTime) {
        this.startTime = this.addedVenue.StartTime;
      }
      if (this.addedVenue?.EndTime) {
        this.endTime = this.addedVenue.EndTime;
      }
      if (this.addedVenue?.date) {
        this.date = this.addedVenue.date;
      }
    },
    startTime(newVal) {
      const capped = this.capTimeToWorkingHours(newVal, "start");
      if (capped.getTime() !== newVal.getTime()) {
        this.startTime = capped;
        return;
      }
      this.endTime = this.normalizeEndTime(this.endTime);
      this.validateBookingTime();
      this.$nextTick(() => this.updateSearchDate());
    },
    endTime(newVal) {
      this.applyEndTimeConstraints(newVal);
    },
    bookedEvents: {
      deep: true,
      handler(newEvents) {
        if (!this.userHasCustomizedSchedule) {
          const hasEvents = (newEvents || []).length > 0;
          if (!this.hasAppliedInitialSchedule) {
            this.syncInitialScheduleFromAvailability();
          } else if (hasEvents && !this.scheduledBeforeBookedEventsLoaded) {
            this.syncInitialScheduleFromAvailability({ refineForBookedEvents: true });
          }
        }
        this.$nextTick(() => this.updateSearchDate());
      },
    },
  },
  computed: {
    isUnavailableDay() {
      if (!this.date) {
        return false; // If no date is selected, don't show as unavailable
      }
      const jsDay = this.date.getDay();
      const arrayIdx = (jsDay + 6) % 7;
      const w = this.workingHoursArray[arrayIdx];
      return w && w.starttime === "00:00" && w.endtime === "00:00";
    },
   
    computedMinDate() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let minDate = today;

      if (this.daysInAdvanceEnabled && this.minDaysInAdvance > 0) {
        const advanceMin = new Date(today);
        advanceMin.setDate(today.getDate() + this.minDaysInAdvance);
        if (advanceMin > minDate) {
          minDate = advanceMin;
        }
      }

      if (this.hoursInAdvanceEnabled && this.minHoursInAdvance > 0) {
        const earliestBooking = this.getEarliestBookingDateTime();
        const earliestDay = new Date(earliestBooking);
        earliestDay.setHours(0, 0, 0, 0);
        if (earliestDay > minDate) {
          minDate = earliestDay;
        }
      }

      if (this.selectedElement && this.selectedElement.startDate) {
        // Parse DD-MM-YYYY format correctly
        const dateParts = this.selectedElement.startDate.split('-');
        if (dateParts.length === 3) {
          const day = parseInt(dateParts[0]);
          const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
          const year = parseInt(dateParts[2]);
          const availabilityStart = new Date(year, month, day);
          availabilityStart.setHours(0, 0, 0, 0);
          if (!isNaN(availabilityStart.getTime()) && availabilityStart > minDate) {
            minDate = availabilityStart;
          }
        }
      }

      return minDate;
    },
    
    computedMaxDate() {
      if (this.selectedElement && this.selectedElement.endDate) {
        // Parse DD-MM-YYYY format correctly
        const dateParts = this.selectedElement.endDate.split('-');
                if (dateParts.length === 3) {
          const day = parseInt(dateParts[0]);
          const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
          const year = parseInt(dateParts[2]);
          const d = new Date(year, month, day);
          return isNaN(d.getTime()) ? null : d;
        }
      }
      // Fix: Set to end of day to avoid time component issues
      const fallbackDate = new Date();
      fallbackDate.setFullYear(fallbackDate.getFullYear() + 1);
      fallbackDate.setHours(23, 59, 59, 999); // Set to end of day
      return fallbackDate;
    },

    endMinuteIncrement() {
      return this.getEndMinuteIncrement(this.endTime.getHours());
    },

    endTimePickerKey() {
      const closing = this.parseWorkingTime(
        this.getWorkingHoursForSelectedDay().endtime
      );
      return `end-${this.endTime.getHours()}-${closing.hours}-${closing.minutes}`;
    },

    nextSevenDays() {
      const days = [];
      const minDate = this.computedMinDate
        ? new Date(this.computedMinDate)
        : new Date();
      minDate.setHours(0, 0, 0, 0);

      for (let i = 0; i < 7; i += 1) {
        const candidate = new Date(minDate);
        candidate.setDate(minDate.getDate() + i);
        const disabled = this.isQuickDateDisabled(candidate);
        days.push({
          key: formatLocalDateKey(candidate),
          date: candidate,
          dow: candidate
            .toLocaleDateString(BOOKING_LOCALE, { weekday: "short" })
            .toUpperCase(),
          dayNum: candidate.getDate(),
          disabled,
        });
      }
      return days;
    },

    quickTimeSlots() {
      if (this.validFullday || !this.date) {
        return [];
      }

      const earliestForDay = this.isSameCalendarDay(
        this.date,
        this.getEarliestBookingDateTime()
      )
        ? this.getEarliestBookingDateTime()
        : null;

      return getAvailableHourSlots({
        date: this.date,
        workingHoursArray: this.workingHoursArray,
        bookedEvents: this.bookedEvents || [],
        earliestDateTime: earliestForDay,
        maxSlots: 8,
      });
    },

    hasNoAvailableSlots() {
      if (this.validFullday || !this.date || this.isUnavailableDay) {
        return false;
      }
      if (!this.workingHoursArray?.length) {
        return false;
      }
      return this.quickTimeSlots.length === 0;
    },

    noSlotsMessage() {
      return this.isSameCalendarDay(this.date, new Date())
        ? "No slots available for today."
        : "No slots available for the selected date.";
    },
  },
  methods: {
    isSameCalendarDay(a, b) {
      if (!a || !b) return false;
      return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
      );
    },

    isQuickDateDisabled(candidate) {
      if (this.computedMaxDate) {
        const max = new Date(this.computedMaxDate);
        max.setHours(23, 59, 59, 999);
        if (candidate > max) return true;
      }

      const jsDay = candidate.getDay();
      const arrayIdx = (jsDay + 6) % 7;
      const w = this.workingHoursArray[arrayIdx];
      return Boolean(w && w.starttime === "00:00" && w.endtime === "00:00");
    },

    markScheduleCustomized() {
      this.userHasCustomizedSchedule = true;
    },

    selectQuickDate(candidate) {
      if (this.isQuickDateDisabled(candidate)) return;
      this.markScheduleCustomized();
      this.date = new Date(candidate);
      this.onDateChange(this.date, { fromUser: false });
      this.$nextTick(() => this.applyFirstAvailableSlot());
    },

    isQuickSlotSelected(slot) {
      return (
        this.startTime.getHours() === slot.startHour &&
        this.startTime.getMinutes() === slot.startMinute &&
        this.endTime.getHours() === slot.endHour &&
        this.endTime.getMinutes() === slot.endMinute
      );
    },

    selectQuickSlot(slot, { fromUser = false } = {}) {
      if (fromUser) {
        this.markScheduleCustomized();
      }
      const start = new Date("2012/10/09 00:00");
      start.setHours(slot.startHour, slot.startMinute, 0, 0);
      const end = new Date("2012/10/09 00:00");
      end.setHours(slot.endHour, slot.endMinute, 0, 0);
      this.startTime = start;
      this.endTime = this.normalizeEndTime(end);
      this.validateBookingTime();
      this.$nextTick(() => this.updateSearchDate());
    },

    applyFirstAvailableSlot() {
      const slots = this.quickTimeSlots;
      if (!slots.length) {
        this.getValidHoursByDay();
        return;
      }

      const matching = slots.find((slot) => this.isQuickSlotSelected(slot));
      if (matching) {
        this.updateSearchDate();
        return;
      }

      this.selectQuickSlot(slots[0]);
    },

    syncInitialScheduleFromAvailability({ refineForBookedEvents = false } = {}) {
      if (this.validFullday) {
        return;
      }

      if (!this.workingHoursArray?.length) {
        return;
      }

      if (this.userHasCustomizedSchedule) {
        return;
      }

      const hasEvents = (this.bookedEvents || []).length > 0;

      if (this.hasAppliedInitialSchedule) {
        if (!refineForBookedEvents || this.scheduledBeforeBookedEventsLoaded) {
          return;
        }
      }

      this.hasAppliedInitialSchedule = true;
      if (hasEvents) {
        this.scheduledBeforeBookedEventsLoaded = true;
      }

      const fromDate = this.computedMinDate
        ? new Date(this.computedMinDate)
        : new Date();
      fromDate.setHours(0, 0, 0, 0);

      const bestDate = findFirstDateWithAvailableSlots({
        fromDate,
        maxDays: 21,
        workingHoursArray: this.workingHoursArray,
        bookedEvents: this.bookedEvents || [],
        earliestDateTime: this.getEarliestBookingDateTime(),
        maxDate: this.computedMaxDate,
      });

      this.date = new Date(bestDate);
      this.getValidHoursByDay({ preserveTimes: true });
      this.$nextTick(() => {
        this.applyFirstAvailableSlot();
        this.$emit("dateSelected", this.formatDateForAPI(this.date));
      });
    },

    onCustomTimeChange() {
      this.markScheduleCustomized();
      this.updateSearchDate();
    },

    getInitialDate() {
      try {
        if (this.selectedDate && typeof this.selectedDate === 'string') {
          const dateParts = this.selectedDate.split('/');
          if (dateParts.length === 3) {
            const day = parseInt(dateParts[0]);
            const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
            const year = parseInt(dateParts[2]);
            const date = new Date(year, month, day);
            if (!isNaN(date.getTime())) {
              return date;
            }
          }
        }
      } catch {
        // selectedDate prop is optional
      }
      return new Date();
    },
    getEarliestBookingDateTime() {
      const now = new Date();
      if (!this.hoursInAdvanceEnabled || !this.minHoursInAdvance) {
        return now;
      }
      return new Date(now.getTime() + this.minHoursInAdvance * 60 * 60 * 1000);
    },

    getBookingStartDateTime() {
      if (!this.date) {
        return null;
      }

      const bookingStart = new Date(this.date);
      bookingStart.setHours(
        this.startTime.getHours(),
        this.startTime.getMinutes(),
        0,
        0
      );
      return bookingStart;
    },

    getValidHours(start, end) {
      let validateHours = [];
      for (let index = start; index <= end; index++) {
        validateHours.push(index);
      }
      return validateHours;
    },

    parseWorkingTime(timeStr) {
      const [hoursPart, minutesPart] = (timeStr || "00:00").split(":");
      const hours = parseInt(hoursPart, 10) || 0;
      const minutes = parseInt(minutesPart, 10) || 0;
      return {
        hours,
        minutes,
        totalMinutes: hours * 60 + minutes,
      };
    },

    getWorkingHoursForSelectedDay() {
      if (!this.date) {
        return { starttime: "09:00", endtime: "17:00" };
      }
      const dayName = getBookingWeekdayName(this.date);
      return (
        this.workingHoursArray.find((d) => d.day === dayName) || {
          starttime: "09:00",
          endtime: "17:00",
        }
      );
    },

    capTimeToWorkingHours(time, type) {
      const workingHours = this.getWorkingHoursForSelectedDay();
      const opening = this.parseWorkingTime(workingHours.starttime);
      const closing = this.parseWorkingTime(workingHours.endtime);
      const totalMinutes = time.getHours() * 60 + time.getMinutes();

      if (type === "start" && totalMinutes < opening.totalMinutes) {
        const capped = new Date(time);
        capped.setHours(opening.hours, opening.minutes, 0, 0);
        return capped;
      }

      if (type === "end") {
        if (totalMinutes > closing.totalMinutes) {
          const capped = new Date(time);
          capped.setHours(closing.hours, closing.minutes, 0, 0);
          return capped;
        }
        return this.snapEndMinutesForClosingHour(time);
      }

      return time;
    },

    getEndMinuteIncrement(hour) {
      const closing = this.parseWorkingTime(
        this.getWorkingHoursForSelectedDay().endtime
      );
      if (hour === closing.hours && closing.minutes === 0) {
        return 60;
      }
      return 30;
    },

    snapEndMinutesForClosingHour(time) {
      const closing = this.parseWorkingTime(
        this.getWorkingHoursForSelectedDay().endtime
      );
      if (time.getHours() !== closing.hours) {
        return time;
      }
      if (time.getMinutes() <= closing.minutes) {
        return time;
      }
      const snapped = new Date(time);
      snapped.setMinutes(closing.minutes, 0, 0);
      return snapped;
    },

    normalizeEndTime(time) {
      let normalized = this.capTimeToWorkingHours(time, "end");
      normalized = this.snapEndMinutesForClosingHour(normalized);

      const startTotal =
        this.startTime.getHours() * 60 + this.startTime.getMinutes();
      const endTotal =
        normalized.getHours() * 60 + normalized.getMinutes();
      if (endTotal <= startTotal) {
        const workingHours = this.getWorkingHoursForSelectedDay();
        const closing = this.parseWorkingTime(workingHours.endtime);
        let desiredEnd = startTotal + 60;
        if (desiredEnd > closing.totalMinutes) {
          desiredEnd = closing.totalMinutes;
        }
        if (desiredEnd <= startTotal) {
          desiredEnd = Math.min(startTotal + 30, closing.totalMinutes);
        }
        normalized = new Date(time);
        normalized.setHours(
          Math.floor(desiredEnd / 60),
          desiredEnd % 60,
          0,
          0
        );
        normalized = this.snapEndMinutesForClosingHour(normalized);
      }

      return normalized;
    },

    applyEndTimeConstraints(time) {
      const normalized = this.normalizeEndTime(time);
      if (normalized.getTime() !== time.getTime()) {
        this.endTime = normalized;
        return;
      }
      this.validateBookingTime();
      this.$nextTick(() => this.updateSearchDate());
    },

    onEndTimeUpdate(value) {
      if (!value) {
        return;
      }
      this.markScheduleCustomized();
      this.applyEndTimeConstraints(value);
    },

    setEndTimeFromStart(startHour, startMinute) {
      const workingHours = this.getWorkingHoursForSelectedDay();
      const closing = this.parseWorkingTime(workingHours.endtime);
      const startTotal = startHour * 60 + startMinute;
      let endTotal = startTotal + 60;

      if (endTotal > closing.totalMinutes) {
        endTotal = closing.totalMinutes;
      }

      const end = new Date("2012/10/09 00:00");
      end.setHours(Math.floor(endTotal / 60), endTotal % 60, 0, 0);
      this.endTime = this.normalizeEndTime(end);
    },

    validateEndHours(hour) {
      if (!this.validateHours.includes(hour)) {
        return false;
      }
      const closing = this.parseWorkingTime(
        this.getWorkingHoursForSelectedDay().endtime
      );
      if (hour > closing.hours) {
        return false;
      }
      return true;
    },

    onEndTimeChange() {
      this.markScheduleCustomized();
      this.applyEndTimeConstraints(this.endTime);
    },

    getValidHoursByDay({ preserveTimes = false } = {}) {
      if (!preserveTimes) {
        this.changeCurrentDataTime();
      }

      // Add null check for this.date
      if (!this.date) {
        this.date = new Date();
      }
      
      const jsDay = this.date.getDay(); // JS: 0=Sunday, 1=Monday, ..., 6=Saturday
      const dayNumber = (jsDay + 6) % 7; // Your format: 0=Monday, ..., 6=Sunday
      const workingHours = this.getWorkingHoursConfigByDay(dayNumber);

      // Parse start and end hours from "HH:mm" with fallback
      const startHour = workingHours.starttime
        ? parseInt(workingHours.starttime.split(":")[0], 10)
        : 9;
      const endHour = workingHours.endtime
        ? parseInt(workingHours.endtime.split(":")[0], 10)
        : 17;
      this.validateHours = this.getValidHours(startHour, endHour);
      this.endTime = this.normalizeEndTime(this.endTime);
      this.updateSearchDate();
    },

    getWorkingHoursConfigByDay(day) {
      // day: 0 (Monday) to 6 (Sunday)
      if (!this.workingHoursArray.length)
        return { starttime: "09:00", endtime: "17:00" };

      // Directly access by index
      const config = this.workingHoursArray[day];
      return config || { starttime: "09:00", endtime: "17:00" };
    },

    isTimeValid(startHours, startMin, endHours, endMin) {
      if (endHours > startHours) return true;
      else if (endHours < startHours) return false;
      else {
        if (endMin > startMin) return true;
        else return false;
      }
    },

    getFullDayStartDateTime(workingHours) {
      if (!this.date) {
        return null;
      }

      const startParts = (workingHours.starttime || "09:00").split(":");
      const startH = parseInt(startParts[0], 10) || 9;
      const startM = parseInt(startParts[1], 10) || 0;
      const fullDayStartTime = new Date(this.date);
      fullDayStartTime.setHours(startH, startM, 0, 0);
      return fullDayStartTime;
    },

    updateSearchDate() {
      if (this.isUnavailableDay) {
        this.invalidBooking = true;
        this.timeErrorType = "unavailable_day";
        this.errorMessageTime =
          "This venue is unavailable on the selected day. Please choose another date.";
        this.$emit("search", {
          errorMessageTime: this.errorMessageTime,
          timeErrorType: this.timeErrorType,
          nonFormatStartDate: this.getBookingStartDateTime(),
          invalidBooking: true,
          nonFormatEndDate: this.endTime,
          nonFormatDate: this.date,
          errorMessage: this.showError,
          bookingTimeError: this.bookingTimeError,
          bookingTimeErrorMessage: this.bookingTimeErrorMessage,
          otpUserBookingHours: this.otpUserBookingHours,
          startHours: "",
          endHours: "",
          startDate: "",
          endDate: "",
          timeRangeValid: false,
          validTime: false,
        });
        return;
      }

      let currentStartHour = this.startTime.getHours().toString().padStart(2, "0");
      let currentStartMinutes = this.startTime.getMinutes().toString().padStart(2, "0");
      let currentEndHour = this.endTime.getHours().toString().padStart(2, "0");
      let currentMinutes = this.endTime
        .getMinutes()
        .toString()
        .padStart(2, "0");
      let checkSelectedDateName = getBookingWeekdayName(this.date);
      let currentStartTime = currentStartHour + currentStartMinutes;
      let currentEndTime = currentEndHour + currentMinutes;

      // Get working hours for the selected day from the new array structure
      const workingHours = this.workingHoursArray.find(
        (d) => d.day === checkSelectedDateName
      ) || { starttime: "09:00", endtime: "17:00" };
      const dayStartTime = (workingHours.starttime || "09:00").replace(":", "");
      const dayEndTime = (workingHours.endtime || "17:00").replace(":", "");

      if (!this.checkValidation && this.hasNoAvailableSlots) {
        this.invalidBooking = true;
        this.timeErrorType = "no_slots_available";
        this.errorMessageTime = this.noSlotsMessage;
      } else if (parseInt(currentStartTime) < parseInt(dayStartTime)) {
        this.invalidBooking = true;
        this.timeErrorType = "start_before_opening";
        this.errorMessageTime = `Start Time should be after ${workingHours.starttime}`;
      } else if (parseInt(currentEndTime) > parseInt(dayEndTime)) {
        // End time: must not be after venue closing for the selected day (separate condition)
        this.invalidBooking = true;
        this.timeErrorType = "end_after_closing";
        this.errorMessageTime =
          dayEndTime.slice(0, -2) + ":" + dayEndTime.slice(-2);
      } else {
        this.invalidBooking = false;
        this.timeErrorType = null;
      }

      if (this.checkValidation === true) {
        // For full day bookings, use venue's working hours for the selected day (same lookup as above by day name)
        const fullDayHours = workingHours;
        const endParts = (fullDayHours.endtime || "17:00").split(":");
        const endH = parseInt(endParts[0], 10) || 17;
        const endM = parseInt(endParts[1], 10) || 0;

        const fullDayStartTime = this.getFullDayStartDateTime(fullDayHours);
        const fullDayEndTime = new Date(this.date);
        fullDayEndTime.setHours(endH, endM, 0, 0);

        const startHoursStr = fullDayHours.starttime || "09:00";
        const endHoursStr = fullDayHours.endtime || "17:00";
        const dateStr = `${this.formatDateNumber(this.date.getDate())}/${this.formatDateNumber(this.date.getMonth() + 1)}/${this.date.getFullYear()}`;
        const startDateStr = `${dateStr} ${startHoursStr}`;
        const endDateStr = `${dateStr} ${endHoursStr}`;

        const fullDayPayload = {
          errorMessageTime: this.errorMessageTime,
          nonFormatStartDate: fullDayStartTime,
          invalidBooking: this.invalidBooking,
          nonFormatEndDate: fullDayEndTime,
          nonFormatDate: this.date,
          errorMessage: this.showError,
          bookingTimeError: this.bookingTimeError,
          bookingTimeErrorMessage: this.bookingTimeErrorMessage,
          otpUserBookingHours: this.otpUserBookingHours,
          startHours: startHoursStr,
          endHours: endHoursStr,
          startDate: startDateStr,
          endDate: endDateStr,
          timeRangeValid: true,
          validTime: !this.invalidBooking,
        };
        this.$emit("selectedBookingList", fullDayPayload);
        // Emit "search" too so parent's dateSearch is updated (used when Add to Booking is clicked)
        this.$emit("search", fullDayPayload);
      } else {
        const bookingStartDateTime = this.getBookingStartDateTime();
        const timeRangeValid = this.isTimeValid(
          this.startTime.getHours(),
          this.startTime.getMinutes(),
          this.endTime.getHours(),
          this.endTime.getMinutes()
        );
        this.$emit("search", {
          errorMessageTime: this.errorMessageTime,
          timeErrorType: this.timeErrorType,
          nonFormatStartDate: bookingStartDateTime,
          invalidBooking: this.invalidBooking,
          nonFormatEndDate: this.endTime,
          nonFormatDate: this.date,
          errorMessage: this.showError,
          bookingTimeError: this.bookingTimeError,
          bookingTimeErrorMessage: this.bookingTimeErrorMessage,
          otpUserBookingHours: this.otpUserBookingHours,
          startHours: `${this.formatDateNumber(
            this.startTime.getHours()
          )}:${this.formatDateNumber(this.startTime.getMinutes())}`,
          endHours: `${this.formatDateNumber(
            this.endTime.getHours()
          )}:${this.formatDateNumber(this.endTime.getMinutes())}`,
          startDate: `${this.formatDateNumber(
            this.date.getDate()
          )}/${this.formatDateNumber(
            this.date.getMonth() + 1
          )}/${this.date.getFullYear()} ${this.formatDateNumber(
            this.startTime.getHours()
          )}:${this.formatDateNumber(this.startTime.getMinutes())}`,
          endDate: `${this.formatDateNumber(
            this.date.getDate()
          )}/${this.formatDateNumber(
            this.date.getMonth() + 1
          )}/${this.date.getFullYear()} ${this.formatDateNumber(
            this.endTime.getHours()
          )}:${this.formatDateNumber(this.endTime.getMinutes())}`,
          timeRangeValid,
          validTime: !this.invalidBooking && timeRangeValid,
        });
      }
    },

    formatDateNumber(number) {
      if (number <= 9) {
        return "0" + number;
      }
      return `${number}`;
    },

    changeCurrentDataTime() {
      const currentDateTimeStamp = new Date();
      const currentHour = currentDateTimeStamp.getHours();
      const currentMinutes = currentDateTimeStamp.getMinutes();

      // Add null check for this.date
      if (!this.date) {
        this.date = new Date();
      }

      // Only snap times to "now" when the selected day is today.
      if (!this.isSameCalendarDay(this.date, currentDateTimeStamp)) {
        return;
      }

      // Map JS day to your working hours array index (0=Monday, ..., 6=Sunday)
      const jsDay = this.date.getDay();
      const dayIndex = (jsDay + 6) % 7;

      // Get working hours for the selected day
      const workingHours = this.getWorkingHoursConfigByDay(dayIndex);

      const startHour = parseInt(workingHours.starttime.split(":")[0]);
      const endHour = parseInt(workingHours.endtime.split(":")[0]);

      const startMinute = currentMinutes < 30 ? 0 : 30;

      if (currentHour <= startHour) {
        const start = new Date("2012/10/09 00:00");
        start.setHours(startHour, startMinute, 0, 0);
        this.startTime = start;
        this.setEndTimeFromStart(startHour, startMinute);
      } else if (currentHour > startHour && currentHour < endHour) {
        const start = new Date("2012/10/09 00:00");
        start.setHours(currentHour, startMinute, 0, 0);
        this.startTime = start;
        this.setEndTimeFromStart(currentHour, startMinute);
      } else if (currentHour >= endHour) {
        const start = new Date("2012/10/09 00:00");
        start.setHours(startHour, startMinute, 0, 0);
        this.startTime = start;
        this.setEndTimeFromStart(startHour, startMinute);
      }
    },

    // Set initial date to the first open day with an available slot when possible
    setInitialDate() {
      const minDate = this.computedMinDate
        ? new Date(this.computedMinDate)
        : new Date();
      minDate.setHours(0, 0, 0, 0);

      const bestDate = findFirstDateWithAvailableSlots({
        fromDate: minDate,
        maxDays: 21,
        workingHoursArray: this.workingHoursArray,
        bookedEvents: this.bookedEvents || [],
        earliestDateTime: this.getEarliestBookingDateTime(),
        maxDate: this.computedMaxDate,
      });

      this.date = bestDate;

      if (this.computedMinDate && bestDate > new Date()) {
        this.navigateToMonth(bestDate);
      }
    },

    // Navigate DatePicker to a specific month
    navigateToMonth(targetDate) {
      if (!this.$refs.datePicker) {
        return;
      }
      
      // Try different methods to navigate the DatePicker
      if (this.$refs.datePicker.move) {
        this.$refs.datePicker.move(targetDate);
      } else if (this.$refs.datePicker.focusDate) {
        this.$refs.datePicker.focusDate(targetDate);
      } else if (this.$refs.datePicker.pageForDate) {
        this.$refs.datePicker.pageForDate(targetDate);
      } else {
        // Fallback: try to set the active date
        if (this.$refs.datePicker.activeDate !== undefined) {
          this.$refs.datePicker.activeDate = targetDate;
        }
      }
    },

    validateBookingTime() {
      if (this.validFullday === true) {
        return;
      }
      
      // Reset error states
      this.bookingTimeError = false;
      this.bookingTimeErrorMessage = "";
      this.showError = false;
      

      const startMinutes =
        this.startTime.getHours() * 60 + this.startTime.getMinutes();
      const endMinutes =
        this.endTime.getHours() * 60 + this.endTime.getMinutes();
      const timeDifferenceInHours = (endMinutes - startMinutes) / 60;
      const timeRangeValid = this.isTimeValid(
        this.startTime.getHours(),
        this.startTime.getMinutes(),
        this.endTime.getHours(),
        this.endTime.getMinutes()
      );

      if (!timeRangeValid) {
        return;
      }

      // Get minimum initial booking time from props (default to 1 hour if not provided)
      let minInitialHours = Number(this.initialBookingAllowed || 1);
      
      
      // Check if the booking meets the minimum initial time requirement
      if (timeDifferenceInHours < minInitialHours) {
        this.bookingTimeError = true;
        this.showError = true;
        
        // Get additional booking allowed from props (default to 0.5 hours if not provided)
        let additionalHours = Number(this.additionalBookingAllowed || 0.5);
        
        // Create dynamic error message
        this.bookingTimeErrorMessage = `Minimum initial booking ${minInitialHours} hour${minInitialHours > 1 ? 's' : ''} with additional ${additionalHours} hour${additionalHours > 1 ? 's' : ''} allowed`;
      }
      
      // Check OTP user booking hours restriction
      if (this.otpUserBookingHours !== null && this.otpUserBookingHours !== undefined) {
        if (this.otpUserBookingHours === 0) {
          // User has no booking hours left
          this.bookingTimeError = true;
          this.showError = true;
          this.bookingTimeErrorMessage = "Logged in user has no booking hours left";
        } else if (timeDifferenceInHours > this.otpUserBookingHours) {
          // User exceeds their booking hours limit
          this.bookingTimeError = true;
          this.showError = true;
          this.bookingTimeErrorMessage = `Logged in user is restricted to ${this.otpUserBookingHours} hour${this.otpUserBookingHours > 1 ? 's' : ''} maximum booking time`;
        }
      }
    },
    onDateChange(newDate, { fromUser = true } = {}) {
      if (newDate) {
        if (fromUser) {
          this.markScheduleCustomized();
        }
        this.date = newDate;
        const formattedDate = this.formatDateForAPI(newDate);
        this.$emit("dateSelected", formattedDate);
        this.getValidHoursByDay({ preserveTimes: !fromUser });
        this.$nextTick(() => {
          this.applyFirstAvailableSlot();
          this.updateSearchDate();
        });
      }
    },
    formatDateForAPI(date) {
      if (!date) return null;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
  },

  created: async function () {
    this.validFullday = this.checkValidation;
    
    // Fetching data from vuex store

    const venueType = decodeURIComponent(
      location.search.split("venueType=")[1].replace(/_/g, " ")
    );
    this.selectedvenueType = venueType;

    // Fetching the venue names to show on the calendar
    const bookingGeneral = getBookingGeneral() || {};
    this.circleColor = bookingGeneral.color;
    
    const selectedVenueId = sessionStorage.getItem("selectedvenuetypeId") || "";
    
    if (selectedVenueId) {
      let newBookingHours = await getVenueWorkingHours(selectedVenueId);
      
      this.workingHoursArray = Array.isArray(newBookingHours?.workinghours)
        ? newBookingHours.workinghours
        : [];
    } else {
      this.workingHoursArray = [];
    }

    this.getValidHoursByDay();

    // Set initial date to a valid date within the allowed range
    this.setInitialDate();
  },

  mounted() {
    // Ensure date restrictions are applied after component is mounted
    this.$nextTick(() => {
      if (this.computedMinDate && this.computedMinDate > new Date()) {
        this.navigateToMonth(this.computedMinDate);
      }
      this.syncInitialScheduleFromAvailability();
    });
  },
};
</script>
<style src="./datesearch.css"></style>
